import { useEffect, useState, forwardRef } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRegisterSW } from "virtual:pwa-register/react";

export const UpdatePrompt = forwardRef<HTMLDivElement>(function UpdatePrompt(_, ref) {
  const [showUpdate, setShowUpdate] = useState(false);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log("SW Registered:", r);
    },
    onRegisterError(error) {
      console.log("SW registration error", error);
    },
  });

  useEffect(() => {
    if (needRefresh) {
      // Auto-reload for new users who have never cached the app before
      // (no cached version means safe to reload immediately)
      const hasVisitedBefore = localStorage.getItem("app_visited");
      if (!hasVisitedBefore) {
        updateServiceWorker(true);
        return;
      }
      setShowUpdate(true);
    }
  }, [needRefresh, updateServiceWorker]);

  // Mark as visited so future updates show the prompt instead of auto-reloading
  useEffect(() => {
    localStorage.setItem("app_visited", "1");
  }, []);

  const handleUpdate = () => {
    updateServiceWorker(true);
    setShowUpdate(false);
  };

  const handleDismiss = () => {
    setShowUpdate(false);
    setNeedRefresh(false);
  };

  if (!showUpdate) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-fade-in">
      <div className="bg-card border border-border rounded-xl shadow-lg p-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">Update Available</p>
            <p className="text-xs text-muted-foreground">A new version is ready</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={handleDismiss}>
              Later
            </Button>
            <Button size="sm" onClick={handleUpdate}>
              Refresh
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});
